import { configureCache, GrowthBook, GrowthBookPayload } from '@growthbook/growthbook';
import { isAutomationUserAgent, isBotUserAgent, isBrowser, isValidUrl } from '@sortlist-frontend/utils';

import { ACCEPT_HEADER, ACCEPT_HEADER_DOCUMENT_TYPE, GB_COOKIE_NAME } from './constants';
import { Domain } from './types';
import { domainToMarket, getConfiguredFeaturesWithExperiments, logInit } from './utils';

export type ServerSidePayloadRequest = {
  headers: Record<string, string | string[] | undefined>;
  cookies?: Record<string, string | undefined>;
};

const firstHeaderValue = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value);

/**
 * This function is used in the `getInitialProps` in the `_app.tsx` file to evaluate experiments on the server side
 * as they come in. It returns the payload and the Growthbook UUID.
 */
export async function getServerSidePayload(url: string, req: ServerSidePayloadRequest) {
  let payload: GrowthBookPayload | undefined;
  const gbUuid = req.cookies?.[GB_COOKIE_NAME];
  const market = isValidUrl(url) ? domainToMarket(new URL(url).hostname as Domain) : undefined;

  if (
    shouldEvaluateSSRExperiments(url, {
      accept: firstHeaderValue(req.headers[ACCEPT_HEADER]),
      userAgent: firstHeaderValue(req.headers['user-agent']),
    }) &&
    gbUuid != null
  ) {
    if (process.env.DISABLE_GROWTHBOOK_CACHE === 'true') {
      configureCache({ disableCache: true });
    }
    const gb = new GrowthBook({
      apiHost: process.env.NEXT_PUBLIC_GROWTHBOOK_API_HOST,
      clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY,
      attributes: {
        id: gbUuid,
        market,
      },
    });

    const res = await gb.init({ timeout: 1000 });
    logInit(res, 'server');

    payload = gb.getPayload();

    // Cleanup
    gb.destroy();
  }

  return { payload, gbUuid };
}

export function shouldEvaluateSSRExperiments(
  url: string,
  headers: {
    accept?: string;
    userAgent?: string;
  },
) {
  if (isBrowser() || !isValidUrl(url)) return false;

  const { serverOnlyConfiguredExperiments } = getConfiguredFeaturesWithExperiments(url);

  const hasServerOnlyConfiguredExperiments = serverOnlyConfiguredExperiments.length > 0;
  const isDocumentRequest = headers.accept?.includes(ACCEPT_HEADER_DOCUMENT_TYPE) ?? false;
  const isRequestBot = isBotUserAgent(headers.userAgent);
  const isRequestAutomation = isAutomationUserAgent(headers.userAgent);
  return hasServerOnlyConfiguredExperiments && isDocumentRequest && !isRequestBot && !isRequestAutomation;
}
